import React from "react";
import PropTypes from "prop-types";
import {useParams} from "react-router-dom";
import {getCampaign} from "../../Services/campaignFunctions";
import {fetchNumber} from "../../Services/numberFunctions";
import {getMe} from "../../Services/operatorFunctions";
import WS from "../../ws";

/**
 * Contexto do Group Manager.
 * Busca a campanha (nome + number_id) e o número conectado (telefone) e
 * disponibiliza para a tela. Faz apenas fetch + estado — formatação e
 * derivações ficam na camada de apresentação (ManagerTopBar).
 *
 * Atenção às semânticas de erro OPOSTAS das duas funções:
 *  - getCampaign engole o erro e retorna `undefined` (não lança).
 *  - fetchNumber NÃO tem catch e LANÇA em falha.
 * Por isso o null-guard em `c?.number_id` e o try/catch no fetchNumber, com
 * degradação independente (nome e telefone caem sozinhos).
 */
const GroupManagerContext = React.createContext({
    campaign: null,
    number: null,
    loading: true,
    selectedGroup: null,
    setSelectedGroup: () => {},
    membersCountByGroup: {},
    me: null,
    role: null,
    permissions: null,
    fullName: null,
    isOwner: false,
    can: () => false,
});

export const GroupManagerProvider = ({children}) => {
    const {id} = useParams();
    const [campaign, setCampaign] = React.useState(null);
    const [number, setNumber] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [selectedGroup, setSelectedGroup] = React.useState(null);
    // Contagem de membros ao vivo por grupo (push via WebSocket) — sobrepõe group.members_count.
    const [membersCountByGroup, setMembersCountByGroup] = React.useState({});
    // Perfil do principal na campanha (owner/operador), pra gatear a UI.
    const [me, setMe] = React.useState(null);

    // Assinatura do WS de contagem (uma só pro provider inteiro).
    React.useEffect(() => {
        const unsub = WS.subscribe((msg) => {
            if (msg?.subject === "group_members" && msg.data?.group_id != null) {
                setMembersCountByGroup((prev) => ({...prev, [msg.data.group_id]: msg.data.members_count}));
            }
        });
        return () => { if (typeof unsub === "function") unsub(); };
    }, []);

    // Reset ao trocar de campanha (higiene do mapa).
    React.useEffect(() => { setMembersCountByGroup({}); }, [id]);

    // Sessão de operador não tem WS de owner (não é user-scoped): abre o WS da campanha
    // pra receber os eventos de grupo em tempo real (mensagens, contagem). Owner é tratado no Privateroute.
    React.useEffect(() => {
        const ownerToken = localStorage.getItem("token");
        const operatorToken = localStorage.getItem("operator_session_token");
        if (!ownerToken && operatorToken && !WS.initialized) {
            WS.initOperator(operatorToken, id);
        }
    }, [id]);

    // Perfil do principal (owner/operador) pra gatear a UI. Falha engolida (undefined).
    React.useEffect(() => {
        let alive = true;
        setMe(null); // limpa o stale ao trocar de campanha
        (async () => {
            const data = await getMe(id);
            if (alive) setMe(data ?? null);
        })();
        return () => { alive = false; };
    }, [id]);

    React.useEffect(() => {
        let alive = true;

        (async () => {
            setLoading(true);

            const fetchedCampaign = await getCampaign(id); // undefined em erro
            if (!alive) return;
            setCampaign(fetchedCampaign ?? null);

            if (fetchedCampaign?.number_id) {
                try {
                    const fetchedNumber = await fetchNumber(fetchedCampaign.number_id);
                    if (alive) setNumber(fetchedNumber ?? null);
                } catch (error) {
                    if (alive) setNumber(null); // fetchNumber lança → telefone degrada sozinho
                }
            }

            if (alive) setLoading(false);
        })();

        return () => {
            alive = false;
        };
    }, [id]);

    const permissions = me?.permissions ?? null;
    const role = me?.role ?? null;
    const fullName = me?.full_name ?? null;
    const hasOwnerToken = () => localStorage.getItem("token") !== null;
    // owner-optimista: enquanto o /me não chegou, quem tem token de owner vê tudo (sem flicker);
    // operador (sem token) nega até saber.
    const isOwner = permissions ? role === "owner" : hasOwnerToken();
    const can = (perm) => (permissions ? role === "owner" || !!permissions[perm] : hasOwnerToken());

    return (
        <GroupManagerContext.Provider value={{campaign, number, loading, selectedGroup, setSelectedGroup, membersCountByGroup, me, role, permissions, fullName, isOwner, can}}>
            {children}
        </GroupManagerContext.Provider>
    );
};

GroupManagerProvider.propTypes = {
    children: PropTypes.node,
};

export const useGroupManager = () => React.useContext(GroupManagerContext);
